import Phaser from 'phaser';
import { ensureTierTextures, textureKey, TIERS, setTierScale, TABLET_SHAPE_SCALE } from '../game/shapes';
import { createShape, ShapeBody } from '../game/shapeFactory';
import { Spawner } from '../game/spawner';
import { MergeManager } from '../game/mergeManager';
import { ImpactFx } from '../game/impactFx';
import { DeadLine } from '../game/deadLine';
import { ScoreState, addToAllTimeMergeCounts } from '../game/score';
import { SteeringWheel } from '../game/steeringWheel';
import { DeviceTilt } from '../game/deviceTilt';
// Googly eyes are intentionally not imported. The implementation lives at
// game/googlyEyes.ts for a future "funny mode" toggle but stays out of the
// normal play loop.
import { track } from '../services/analytics';
import { sfx, resumeAudio, startMusic } from '../services/audio';
import { isShakeEnabled, isTiltEnabled, isTiltDeviceModeEnabled, isTiltJoystickActive, isTouchDevice, setSetting } from '../services/settings';
import { fmt } from '../util/format';
import { addNeonBackdrop, addRadialGlow } from '../util/glow';
import { ensureWoodTexture } from '../util/wood';
import { drawGear, drawPhone } from '../util/glyphs';
import { activePalette, css, lighten } from '../util/palette';

// Inner play width between the container walls. Phones use the base width;
// tablets and desktop (>= 700px CSS width) get a wider jar so the extra screen
// real estate is actually used. Both are clamped so the FULL container — walls
// included — stays on screen (narrow Androids used to clip ~28px of wall off
// each edge).
const PLAY_WIDTH = 380;
const PLAY_WIDTH_WIDE = 600;
const WIDE_SCREEN_MIN = 700;
// Thicker walls give Matter's solver more depth to push out dynamic bodies
// that briefly tunnel into the wall during a container rotation.
const WALL_THICKNESS = 28;
// Base y positions, pre safe-area inset. The actual values used at runtime
// add the device safe-area inset (Android status bar / iOS notch) read from
// CSS env(safe-area-inset-top) so the top row never sits behind the system UI.
// Layout is now driven from a single TOP_INSET. Each Y constant below is the
// pre-inset offset; final positions are TOP_INSET + the offset.
const Y_BUTTON_ROW = 16;         // tilt + menu buttons (top right)
const Y_LABEL_ROW = 38;          // SCORE / BEST labels
const Y_NUMBER_ROW = 60;         // numbers under labels
const BASE_POWERUP_DOCK_Y = 134;  // reserved for future powerup dock
// Spawn lifted above the dead line so even the longest-tailed spawnable shape
// (pentagon, whose bottom extent is ~0.81 * radius below its center — ~38px at
// the current 47px radius) sits clearly above the dashed dead line in the
// dropper. The container floor doesn't move; we just give the held shape more
// clearance.
const BASE_SPAWN_Y = 178;
const BASE_DEAD_LINE_Y = 230;
// Wide screens scale shapes 1.32x, so the dropper needs proportionally more
// headroom — and tablets have spare vertical space below the jar anyway.
// Scooting the dead line (and with it the whole container) down keeps even a
// held crescent clear of the dashed line.
const WIDE_DEAD_LINE_EXTRA = 48;
const FLOOR_OFFSET = 30;
const MAX_CONTAINER_H = 610; // caps container height on tablets
const DPR = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;
const FONT = 'Fredoka, system-ui, -apple-system, sans-serif';

/** Mid-run state carried across a scene.restart() triggered by a theme change
 *  in the in-game Settings: the chrome is rebuilt in the new palette and the
 *  run continues where it paused. */
interface RunSnapshot {
  score: number;
  mergeCounts: Record<number, number>;
  currentTier: number;
  nextTier: number;
  shapes: { tier: number; x: number; y: number; vx: number; vy: number; angle: number; av: number }[];
}

function readSafeAreaInset(varName: '--sat' | '--sab'): number {
  if (typeof window === 'undefined') return 0;
  try {
    const v = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
    const parsed = parseFloat(v);
    return Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function readSafeAreaInsetTop(): number {
  return readSafeAreaInset('--sat');
}

/** Bottom system-UI inset (Android gesture/3-button nav bar, iOS home
 *  indicator) so the container floor never sits under the nav row. */
function readSafeAreaInsetBottom(): number {
  return readSafeAreaInset('--sab');
}

/** Effective top padding for HUD layout. Adds a constant buffer on top of the
 *  device safe-area inset so the top row never crowds the system UI / URL
 *  bar, even when env(safe-area-inset-top) reports 0 (e.g., mobile web). */
function topPadding(): number {
  return readSafeAreaInsetTop() + 32;
}

export class GameScene extends Phaser.Scene {
  static readonly KEY = 'GameScene';
  private spawner!: Spawner;
  private merger!: MergeManager;
  private impactFx?: ImpactFx;
  private deadLine!: DeadLine;
  private score = new ScoreState();
  private dynamicBodies: ShapeBody[] = [];
  private scoreText!: Phaser.GameObjects.Text;
  private layoutSpawnY = BASE_SPAWN_Y;
  private layoutDockY = BASE_POWERUP_DOCK_Y;
  private layoutButtonY = Y_BUTTON_ROW;
  private layoutNumberY = Y_NUMBER_ROW;

  // First-play tilt tutorial state. The wheel defaults off on mobile to keep
  // the first impression clean; once a few shapes have accumulated we turn
  // the wheel on, show a one-time toast explaining tilt + the fall-out
  // game-over rule, and remember that the tutorial has been seen.
  private static readonly TUTORIAL_KEY = 'shmerge.tutorial.tiltSeen';
  private tutorialFired = false;
  private comboText!: Phaser.GameObjects.Text;
  private comboFadeTimer?: Phaser.Time.TimerEvent;
  private scoreLabelText!: Phaser.GameObjects.Text;
  private nextLabel!: Phaser.GameObjects.Text;
  private nextPreviewSprite?: Phaser.GameObjects.Image;
  private nextPreviewGlow?: Phaser.GameObjects.Image;
  private highScoreText!: Phaser.GameObjects.Text;
  private layoutNextX = 150;
  private layoutNextShapeY = 0;
  private readonly bobDriver = { offset: 0 };
  private gameOver = false;
  private createdPaletteName = '';

  constructor() {
    super({ key: GameScene.KEY });
  }

  create(data?: { restore?: RunSnapshot }): void {
    track('game_started');
    // Phaser reuses the scene instance on restart. Clear callbacks that
    // reference destroyed Text objects from the previous run before reset().
    this.score.onChange = undefined;
    this.score.onHighScore = undefined;
    this.score.reset();
    this.gameOver = false;
    this.dynamicBodies = [];
    this.tiltGauge = 1;
    this.containerAngle = 0;

    // Stop any merger/deadLine from the previous run that may have lingering
    // listeners on the matter world.
    this.merger?.destroy();
    this.impactFx?.destroy();
    this.deadLine?.destroy();
    this.spawner?.destroy();

    const { width, height } = this.scale;
    setTierScale(width >= WIDE_SCREEN_MIN ? TABLET_SHAPE_SCALE : 1.0);
    ensureTierTextures(this);
    const pal = activePalette();
    this.createdPaletteName = pal.name;
    const cx = width / 2;
    // 6px of breathing room outside each wall keeps the whole jar visible.
    const targetPlayW = width >= WIDE_SCREEN_MIN ? PLAY_WIDTH_WIDE : PLAY_WIDTH;
    const playW = Math.min(targetPlayW, width - 2 * (WALL_THICKNESS + 6));
    const leftX = cx - playW / 2;
    const rightX = cx + playW / 2;
    // Push every top-row element down by the device's safe-area inset plus a
    // constant buffer so the HUD and powerup dock clear the Android status
    // bar / iOS notch / mobile-web URL bar with room to breathe.
    const topInset = topPadding();
    const buttonY = Y_BUTTON_ROW + topInset;
    const labelY = Y_LABEL_ROW + topInset;
    const numberY = Y_NUMBER_ROW + topInset;
    const dockY = BASE_POWERUP_DOCK_Y + topInset;
    const spawnY = BASE_SPAWN_Y + topInset;
    const deadLineY = BASE_DEAD_LINE_Y + topInset + (width >= WIDE_SCREEN_MIN ? WIDE_DEAD_LINE_EXTRA : 0);
    // Lift the floor above the Android nav row / iOS home indicator.
    const bottomInset = readSafeAreaInsetBottom();
    const floorY = Math.min(height - FLOOR_OFFSET - bottomInset, deadLineY + MAX_CONTAINER_H);
    this.layoutSpawnY = spawnY;
    this.layoutNumberY = numberY;
    this.layoutDockY = dockY;
    this.layoutButtonY = buttonY;

    // Inner faces of the container (local frame), for the containment net. The
    // side walls sit just outside leftX/rightX so their inner faces are exactly
    // leftX/rightX; the floor's top face is floorY; the mouth (open top) is at
    // deadLineY, where the side walls begin.
    this.innerLeftX = leftX;
    this.innerRightX = rightX;
    this.innerFloorY = floorY;
    this.innerTopY = deadLineY;

    // World bounds: limit physics world to a comfortable area.
    this.matter.world.setBounds(0, 0, width, height + 200, 64, true, true, false, true);

    // Soft fade up on (re)start for a gentle entrance into the playfield.
    this.cameras.main.fadeIn(220, 7, 6, 15);

    // Neon-candy backdrop: glow centered on the jar so the container sits in
    // the brightest part of the lit space. (Shared helper; see util/glow.ts.)
    addNeonBackdrop(this, {
      glowX: cx,
      glowY: (deadLineY + floorY) / 2,
      glowRadius: (floorY - deadLineY) * 0.95,
      bokeh: true,
    });

    // Soft gradient strip at the very top of the canvas — fades the OS status
    // bar / mobile-web URL bar into the playfield so the system UI doesn't
    // sit on a hard edge against the navy background.
    const grad = this.add.graphics().setDepth(2);
    // Light palettes fade from their own lifted top tone — a black fade would
    // read as a smudge over the beige stage.
    const fadeTop = pal.mode === 'light' ? pal.bgTop : 0x000000;
    grad.fillGradientStyle(fadeTop, fadeTop, pal.bgBottom, pal.bgBottom, 0.65, 0.65, 0, 0);
    grad.fillRect(0, 0, width, Math.max(28, topInset + 8));

    // U-shaped container: two side walls + a floor. Rotatable as a rigid group
    // via the wheel — gravity stays straight down, so tilting the container
    // makes shapes slide along the now-angled floor. The body is the single
    // source of truth; the visual is synced from body.position in the
    // matter `afterupdate` hook (see end of create()).
    // Wall styling: deeper navy fill with a brighter inner-edge highlight so
    // the U-shape reads as a physical container, not as background. The
    // highlight is a thin rectangle parented to the wall position via the
    // afterupdate sync (same as visual) so it rotates with the container.
    // Glassy translucent walls over the glow read as a candy jar; a bright
    // glossy strip on each inner face catches the light and frames the pile.
    const wallColor = pal.jarFill;       // translucent candy-glass wall
    const wallHighlight = pal.jarEdge;   // glass inner-edge highlight
    const NEON = pal.jarRim;             // glossy inner highlight streak
    this.containerPivot = { x: cx, y: (deadLineY + floorY) / 2 };

    // Ambient soft drop shadow behind the jar so it reads as a physical vessel
    // sitting in the lit space. Drawn at rest (doesn't track the brief tilt
    // rotation) at a depth just above the backdrop.
    const jarShadow = this.add.graphics().setDepth(-1);
    jarShadow.fillStyle(pal.chipShadow, 0.3);
    jarShadow.fillRoundedRect(
      leftX - WALL_THICKNESS - 6,
      deadLineY + 6,
      playW + WALL_THICKNESS * 2 + 12,
      floorY - deadLineY + WALL_THICKNESS + 10,
      26,
    );

    this.walls = [];
    const addWall = (
      x: number,
      y: number,
      w: number,
      h: number,
      opts: { floorRim?: boolean; sideRim?: 'left' | 'right' } = {},
    ) => {
      let visual: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Image;
      if (pal.wood) {
        // Wooden-crate treatment: opaque grained planks (grain runs along each
        // wall's long axis) instead of translucent candy glass.
        const woodKey = ensureWoodTexture(this, w, h, pal.wood, opts.floorRim ? 'x' : 'y');
        visual = this.add.image(x, y, woodKey).setDisplaySize(w, h).setDepth(0);
      } else {
        const glass = this.add.rectangle(x, y, w, h, wallColor, 0.42).setDepth(0);
        glass.setStrokeStyle(1.5, wallHighlight, 0.55);
        visual = glass;
      }
      const body = this.matter.add.rectangle(x, y, w, h, { isStatic: true, friction: 0.3 });
      // Neon inner-edge strip. Floor: a horizontal bar along its top face.
      // Side walls: a vertical bar along their inner face. Both ride the
      // container rotation via the afterupdate sync below.
      let rim: Phaser.GameObjects.Rectangle | undefined;
      let rimOffsetX = 0;
      let rimOffsetY = 0;
      if (opts.floorRim) {
        rimOffsetY = -h / 2 + 1.5;
        rim = this.add.rectangle(x, y + rimOffsetY, w - 4, 3, NEON, 0.9).setDepth(1);
      } else if (opts.sideRim) {
        rimOffsetX = opts.sideRim === 'left' ? w / 2 - 1.5 : -w / 2 + 1.5;
        rim = this.add.rectangle(x + rimOffsetX, y, 3, h, NEON, 0.85).setDepth(1);
      }
      this.walls.push({ body, visual, restX: x, restY: y, w, h, rim, rimOffsetX, rimOffsetY });
    };
    const wallH = floorY - deadLineY;
    addWall(leftX - WALL_THICKNESS / 2, (deadLineY + floorY) / 2, WALL_THICKNESS, wallH, { sideRim: 'left' });
    addWall(rightX + WALL_THICKNESS / 2, (deadLineY + floorY) / 2, WALL_THICKNESS, wallH, { sideRim: 'right' });
    addWall(cx, floorY + WALL_THICKNESS / 2, playW + WALL_THICKNESS * 2, WALL_THICKNESS, { floorRim: true });

    // After every physics step, copy body.position/angle to the visual so the
    // render uses Matter's final post-step state and never a half-applied
    // intermediate. Removing the prior dual-write in applyContainerRotation()
    // is what kills the per-frame jitter.
    this.afterUpdateHandler = () => {
      // Safety net first: undo any wall tunneling the solver allowed this step,
      // before the visuals are synced from body positions.
      this.containBodies();
      for (const w of this.walls) {
        w.visual.setPosition(w.body.position.x, w.body.position.y);
        w.visual.setRotation(w.body.angle);
        if (w.rim) {
          // Place the rim relative to the wall body, then rotate the offset
          // around the body to follow container rotation.
          const cos = Math.cos(w.body.angle);
          const sin = Math.sin(w.body.angle);
          const dx = w.rimOffsetX;
          const dy = w.rimOffsetY;
          w.rim.setPosition(w.body.position.x + dx * cos - dy * sin, w.body.position.y + dx * sin + dy * cos);
          w.rim.setRotation(w.body.angle);
        }
      }
    };
    this.matter.world.on('afterupdate', this.afterUpdateHandler);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      // matter.world may already be torn down by the time SHUTDOWN fires
      // during a scene.stop() -> scene.start() retry; guard the off().
      const world = this.matter?.world;
      if (world && this.afterUpdateHandler) world.off('afterupdate', this.afterUpdateHandler);
      this.afterUpdateHandler = undefined;
    });

    // Dead line (UI element + game-over trigger).
    this.deadLine = new DeadLine(this, deadLineY, leftX, rightX);
    this.deadLine.onGameOver = () => this.endGame();

    // Spawner — controls the held shape at top.
    this.spawner = new Spawner(this, { leftX, rightX, spawnY, deadLineY });

    // Merge manager — handles collision events.
    this.merger = new MergeManager(this);
    this.impactFx = new ImpactFx(this);
    this.merger.onMerge = ({ tierIndex, sourceTierIndex, newBody, bonus, x, y }) => {
      if (bonus > 0) {
        this.score.awardBonus(bonus);
      } else {
        const tier = TIERS[tierIndex];
        this.score.awardMerge(tier.score);
      }
      if (newBody) {
        this.score.incrementMergeCount(tierIndex);
        this.dynamicBodies.push(newBody);
      }
      this.earnShakeCharge();
      sfx('merge', { tier: tierIndex });
      if (this.score.comboCount >= 2) sfx('combo', { tier: this.score.comboCount });
      if (sourceTierIndex === 2 || this.score.comboCount >= 2) this.spawnShapeReactions(x, y, sourceTierIndex);
      if (isShakeEnabled()) {
        // Softened tier/combo-scaled shake — keeps the escalation curve but with
        // a gentler ceiling so big merges punch without rattling the screen.
        const intensity = Math.min(0.011, 0.002 + tierIndex * 0.0007 + this.score.comboCount * 0.001);
        const duration = 120 + Math.min(220, tierIndex * 18 + this.score.comboCount * 28);
        this.cameras.main.shake(duration, intensity);
      }
      if (tierIndex === TIERS.length - 1) this.showKingPopup(x, y);
    };

    // ── Top-left HUD: SCORE column, then NEXT column to its right ──
    // Both labels share the button row Y; score number sits below; next shape
    // sits 4 px below its label.
    const labelCol = css(pal.textLabel);
    const valueCol = css(pal.text);
    const scoreColX = 58;
    const nextColX = 150;
    this.layoutNextX = nextColX;
    // chipRowY: vertical centre of the score pill and the NEXT column chip area.
    // contentRowY: where the score number and next-shape sprite are drawn —
    // shifted 3 px lower than the chip centre so they sit optically centred
    // (both previously appeared slightly too high inside their respective areas).
    const chipRowY = buttonY + 29;
    const contentRowY = chipRowY + 3;
    this.layoutNextShapeY = contentRowY;

    // Candy chip behind the score number — dark glassy lozenge with a soft
    // shadow and a top sheen.
    const scoreChip = this.add.graphics().setDepth(58);
    this.drawCandyChip(scoreChip, scoreColX, chipRowY, 104, 38, pal.chipStroke);

    this.scoreLabelText = this.add
      .text(scoreColX, buttonY, 'SCORE', {
        fontFamily: FONT, fontSize: '12px', fontStyle: 'bold',
        color: labelCol, resolution: DPR,
      })
      .setOrigin(0.5, 0.5).setDepth(60);

    this.scoreText = this.add
      .text(scoreColX, contentRowY, '0', {
        fontFamily: FONT, fontSize: '28px', fontStyle: 'bold',
        color: valueCol, resolution: DPR,
      })
      .setOrigin(0.5, 0.5).setDepth(60);

    // Single driver tween — offset applied to score number AND next shape each frame.
    this.bobDriver.offset = 0;
    this.tweens.add({
      targets: this.bobDriver,
      offset: -4,
      duration: 1400, yoyo: true, repeat: -1, ease: 'Sine.easeInOut',
      onUpdate: () => {
        this.scoreText.y = contentRowY + this.bobDriver.offset;
        if (this.nextPreviewSprite) this.nextPreviewSprite.y = this.layoutNextShapeY + this.bobDriver.offset;
        if (this.nextPreviewGlow) this.nextPreviewGlow.y = this.layoutNextShapeY + this.bobDriver.offset;
      },
    });

    this.nextLabel = this.add
      .text(nextColX, buttonY, 'NEXT', {
        fontFamily: FONT, fontSize: '12px', fontStyle: 'bold',
        color: labelCol, resolution: DPR,
      })
      .setOrigin(0.5, 0.5).setDepth(60);

    // highScoreText kept as invisible placeholder so existing references compile.
    this.highScoreText = this.add
      .text(-9999, -9999, '', { fontFamily: FONT, fontSize: '1px', resolution: DPR })
      .setVisible(false).setDepth(0);

    void labelY;
    void dockY;
    void buttonY;

    this.comboText = this.add
      .text(cx, height * 0.57, '', {
        fontFamily: FONT,
        fontSize: '22px',
        fontStyle: 'bold',
        color: css(pal.coin),
        resolution: DPR,
      })
      .setOrigin(0.5)
      .setDepth(60);
    // Candy-pop treatment: warm caramel outline + soft drop shadow so the gold
    // reads as a chunky sugar-coated word, not flat text.
    this.comboText.setStroke('#7a4500', 6);
    this.comboText.setShadow(0, 3, '#2a1800', 6, true, true);

    this.spawner.preview = {
      setTier: (tierIndex) => this.renderNextPreview(tierIndex),
    };
    this.renderNextPreview(this.spawner.getNextTier());

    this.spawner.onDrop = (body) => {
      this.dynamicBodies.push(body);
      this.score.comboCount = 0;
      this.score.comboMultiplier = 1;
      resumeAudio();
      sfx('drop');
    };
    // Block drops while the steering wheel is being dragged.
    this.spawner.shouldDrop = () => !this.wheel?.isActive();

    this.score.onChange = (current, combo) => {
      const increased = current > (parseInt(this.scoreText.text, 10) || 0);
      this.scoreText.setText(fmt(current));
      if (!increased) return;
      // Pop punch on score increase
      this.tweens.add({
        targets: this.scoreText,
        scaleX: 1.22,
        scaleY: 1.22,
        duration: 70,
        yoyo: true,
        ease: 'Back.easeOut',
      });
      this.spawnScoreParticles();
      if (combo > 1) {
        this.comboText.setText(`COMBO x${combo}`);
        this.comboText.setAlpha(1);
        // Bouncy candy pop: overshoot in big, settle with a Back ease, and a
        // little random tilt so each pop feels alive. Escalates with combo.
        const scale = Math.min(1 + (combo - 1) * 0.28, 2.6);
        this.comboText.setScale(scale * 1.18).setAngle((Math.random() - 0.5) * 9);
        this.tweens.killTweensOf(this.comboText);
        this.tweens.add({
          targets: this.comboText,
          scale,
          angle: 0,
          duration: 280,
          ease: 'Back.easeOut',
        });
        // Brief warm screen-glow on bigger combos.
        if (combo >= 3) this.flashComboGlow(combo);
        // Cancel any pending fade and restart the 1.2s linger timer.
        this.comboFadeTimer?.remove();
        this.comboFadeTimer = this.time.delayedCall(1200, () => {
          this.tweens.add({
            targets: this.comboText,
            alpha: 0,
            duration: 300,
            onComplete: () => this.comboText.setText(''),
          });
        });
      }
    };

    // Steering wheel (bottom-left). Visible when tilt is on and mode is joystick.
    this.wheel?.destroy();
    this.wheel = undefined;
    if (isTiltJoystickActive()) {
      const wheelX = 80;
      const wheelY = height - 70 - readSafeAreaInsetBottom();
      this.wheel = new SteeringWheel(this, wheelX, wheelY, 44);
      this.tiltGaugeBg = this.add
        .rectangle(wheelX, wheelY - 60, 90, 5, pal.surface, 0.75)
        .setOrigin(0.5)
        .setDepth(70);
      this.tiltGaugeFill = this.add
        .rectangle(wheelX - 45, wheelY - 60, 90, 5, pal.primary, 0.9)
        .setOrigin(0, 0.5)
        .setDepth(71);
    }

    if (!this.deviceTilt) this.deviceTilt = new DeviceTilt();
    const btnRowY = buttonY + 19; // vertically centred between label and number rows
    this.layoutButtonY = btnRowY;
    this.buildTopRightButtons(width, btnRowY);

    // Toggling TILT or TILT JOYSTICK in Settings while a run is in progress
    // should apply when the player returns. RESUME fires after SettingsScene
    // calls scene.resume('GameScene').
    this.events.on(Phaser.Scenes.Events.RESUME, this.onResume, this);
    this.events.once(Phaser.Scenes.Events.SHUTDOWN, () => {
      this.events.off(Phaser.Scenes.Events.RESUME, this.onResume, this);
    });

    // Rebuilding after an in-game theme change: continue the captured run.
    if (data?.restore) this.restoreRun(data.restore);

    // Debug button: dev-only escape hatch to quickly fill the pile.
    if (import.meta.env.DEV) {
      const debugBtn = this.add
        .rectangle(width - 50, height - 40, 80, 32, 0x44475a, 0.9)
        .setStrokeStyle(1, 0xff6b9d, 0.6)
        .setInteractive({ useHandCursor: true })
        .setDepth(70);
      this.add
        .text(width - 50, height - 40, 'DEBUG FILL', {
          fontFamily: FONT,
          fontSize: '10px',
          color: '#ff6b9d',
          resolution: DPR,
        })
        .setOrigin(0.5)
        .setDepth(71);
      debugBtn.on('pointerup', () => this.debugSpawnBurst(leftX, rightX));
    }

  }

  private escapeImmunity = false;
  private menuBtnObjs: Phaser.GameObjects.GameObject[] = [];
  private tiltBtnObjs: Phaser.GameObjects.GameObject[] = [];
  private playW = 0;
  private leftX = 0;
  private rightX = 0;

  /** Top-right button cluster: tilt toggle + hamburger menu.
   *  Tilt button is always shown on all platforms.
   *  Rebuildable so settings changes applied mid-run take effect on RESUME. */
  private buildTopRightButtons(width: number, buttonY: number): void {
    for (const o of this.menuBtnObjs) o.destroy();
    for (const o of this.tiltBtnObjs) o.destroy();
    this.menuBtnObjs = [];
    this.tiltBtnObjs = [];
    const pal = activePalette();
    const iconAccent = pal.iconAccent ?? pal.primary;

    const size = 45;
    const gap = 8;
    const radius = Math.round(size * 0.32);
    const menuX = width - size / 2 - 8;
    const tiltX = menuX - size - gap;

    // Shared glassy-chip painter — mirrors createIconButton (util/button.ts) so
    // the HUD cog/tilt read as the same component family as the menu chips.
    const paintChip = (g: Phaser.GameObjects.Graphics, x: number, active: boolean) => {
      g.clear();
      g.fillStyle(pal.chipShadow, 0.5);
      g.fillRoundedRect(x - size / 2, buttonY - size / 2 + 3, size, size, radius);
      const top = active ? lighten(pal.toggleOn, 0.22) : lighten(pal.surface, 0.12);
      const bot = active ? pal.toggleOn : pal.surface;
      g.fillGradientStyle(top, top, bot, bot, 0.96, 0.96, 0.96, 0.96);
      g.fillRoundedRect(x - size / 2, buttonY - size / 2, size, size, radius);
      g.lineStyle(1.5, iconAccent, active ? 1 : 0.55);
      g.strokeRoundedRect(x - size / 2, buttonY - size / 2, size, size, radius);
      g.fillStyle(0xffffff, 0.1);
      g.fillRoundedRect(x - size / 2 + 4, buttonY - size / 2 + 3, size - 8, size * 0.4, radius * 0.7);
    };

    // Menu (settings cog) button.
    const menuChip = this.add.graphics().setDepth(60);
    paintChip(menuChip, menuX, false);
    const menuIcon = this.add.graphics().setDepth(61);
    drawGear(menuIcon, menuX, buttonY, iconAccent, 1, pal.surface);
    const menuHit = this.add
      .rectangle(menuX, buttonY, size, size, 0x000000, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(62);
    menuHit.on('pointerdown', () => this.spawner?.suppressNextDrop());
    menuHit.on('pointerup', () => {
      this.spawner?.suppressNextDrop();
      this.scene.launch('SettingsScene', { returnTo: 'GameScene' });
      this.scene.pause();
    });
    this.menuBtnObjs.push(menuChip, menuIcon, menuHit);

    // Tilt button — always visible. Phone icon tilted ~25°.
    const tiltChip = this.add.graphics().setDepth(60);
    const tiltIcon = this.add.container(tiltX, buttonY).setDepth(61).setRotation(0.45);
    const phone = this.add.graphics();
    drawPhone(phone, 0, 0, iconAccent, 1);
    tiltIcon.add(phone);

    const refreshTiltBg = () => paintChip(tiltChip, tiltX, isTiltEnabled());
    refreshTiltBg();

    const statusText = this.add
      .text(tiltX, buttonY + size / 2 + 7, this.tiltStatusGlyph(), {
        fontFamily: FONT,
        fontSize: '9px',
        fontStyle: 'bold',
        color: css(iconAccent),
        resolution: DPR,
      })
      .setOrigin(0.5, 0)
      .setDepth(61);

    this.deviceTilt!.setOnStatusChange(() => statusText.setText(this.tiltStatusGlyph()));

    const tiltHit = this.add
      .rectangle(tiltX, buttonY, size, size, 0x000000, 0)
      .setInteractive({ useHandCursor: true })
      .setDepth(62);
    tiltHit.on('pointerdown', () => this.spawner?.suppressNextDrop());
    tiltHit.on('pointerup', async () => {
      this.spawner?.suppressNextDrop();
      const turningOn = !isTiltEnabled();
      if (turningOn && isTiltDeviceModeEnabled()) {
        await this.deviceTilt!.requestPermission();
        if (!this.deviceTilt!.isEnabled()) return; // permission denied
      } else if (!turningOn) {
        this.deviceTilt!.disable();
      }
      setSetting('tilt', turningOn);
      refreshTiltBg();
      statusText.setText(this.tiltStatusGlyph());
      this.applyLiveSettings();
    });

    this.tiltBtnObjs.push(tiltChip, tiltIcon, statusText, tiltHit);
  }

  private onResume(): void {
    // A theme picked in the in-game Settings can't repaint the paused chrome
    // piecemeal — restart the scene so create() rebuilds everything in the new
    // palette, carrying the run (score, dropper queue, pile) across.
    if (!this.gameOver && activePalette().name !== this.createdPaletteName) {
      this.scene.restart({ restore: this.captureRun() });
      return;
    }
    void startMusic();
    this.applyLiveSettings();
  }

  private captureRun(): RunSnapshot {
    return {
      score: this.score.current,
      mergeCounts: { ...this.score.mergeCounts },
      currentTier: this.spawner.getCurrentTier(),
      nextTier: this.spawner.getNextTier(),
      shapes: this.dynamicBodies
        .filter((b) => b.active && !b.merging)
        .map((b) => {
          const body = b.body as MatterJS.BodyType;
          return {
            tier: b.shapeTier, x: b.x, y: b.y,
            vx: body.velocity.x, vy: body.velocity.y,
            angle: b.rotation, av: body.angularVelocity,
          };
        }),
    };
  }

  private restoreRun(snap: RunSnapshot): void {
    this.score.current = snap.score;
    this.score.mergeCounts = { ...snap.mergeCounts };
    // Set the text directly — going through score.onChange would fire the
    // score-pop punch and particles for what is just a re-render.
    this.scoreText.setText(fmt(snap.score));
    this.spawner.setQueue(snap.currentTier, snap.nextTier);
    for (const s of snap.shapes) {
      const body = createShape(this, s.tier, s.x, s.y);
      body.setDepth(15);
      body.setRotation(s.angle);
      body.setVelocity(s.vx, s.vy);
      body.setAngularVelocity(s.av);
      this.dynamicBodies.push(body);
    }
  }

  private static tutorialSeen(): boolean {
    try {
      return localStorage.getItem(GameScene.TUTORIAL_KEY) === '1';
    } catch {
      return false;
    }
  }

  private static markTutorialSeen(): void {
    try {
      localStorage.setItem(GameScene.TUTORIAL_KEY, '1');
    } catch {}
  }

  private runTiltTutorial(): void {
    const { width, height } = this.scale;
    const cx = width / 2;
    const pal = activePalette();
    const onTouch = isTouchDevice();

    // Mobile: auto-enable device tilt so they experience it right away, not
    // via a button hunt. Desktop has no phone to tilt — the card instead
    // points the player at the TILT toggle (top right) so they turn container
    // rotation on themselves.
    if (onTouch && this.deviceTilt && !this.deviceTilt.isEnabled()) {
      void this.deviceTilt.requestPermission();
    }

    // Small floating card — doesn't cover gameplay, just a gentle nudge.
    const cardW = Math.min(width - 48, onTouch ? 280 : 330);
    const cardH = 110;
    const cardX = cx - cardW / 2;
    const cardY = height * 0.58;

    const card = this.add.graphics().setDepth(150);
    card.fillStyle(pal.surfaceDeep, 0.93);
    card.fillRoundedRect(cardX, cardY, cardW, cardH, 14);
    card.lineStyle(2, pal.primary, 0.85);
    card.strokeRoundedRect(cardX, cardY, cardW, cardH, 14);

    // Animated icon, rocking left/right: a phone on touch devices, a mini
    // candy jar (the thing you're actually rotating) on desktop.
    const glyphX = cardX + 46;
    const glyphY = cardY + cardH / 2;
    const glyph = this.add.graphics().setDepth(151);
    const drawGlyph = (angle: number) => {
      glyph.clear();
      glyph.save();
      glyph.translateCanvas(glyphX, glyphY);
      glyph.rotateCanvas(angle);
      if (onTouch) {
        glyph.fillStyle(0xffffff, 0.18);
        glyph.fillRoundedRect(-13, -22, 26, 44, 4);
        glyph.lineStyle(2, 0xffffff, 0.9);
        glyph.strokeRoundedRect(-13, -22, 26, 44, 4);
        glyph.fillStyle(pal.primary, 0.9);
        glyph.fillCircle(0, 15, 3.5);
      } else {
        // Mini U-container with a candy resting on the floor.
        glyph.fillStyle(0xffffff, 0.18);
        glyph.fillRect(-21, -16, 6, 34);
        glyph.fillRect(15, -16, 6, 34);
        glyph.fillRect(-21, 18, 42, 6);
        glyph.lineStyle(2, 0xffffff, 0.9);
        glyph.strokeRect(-21, -16, 6, 34);
        glyph.strokeRect(15, -16, 6, 34);
        glyph.strokeRect(-21, 18, 42, 6);
        glyph.fillStyle(pal.primary, 0.9);
        glyph.fillCircle(0, 12, 5);
      }
      glyph.restore();
    };
    const rocker = { a: 0 };
    drawGlyph(0);
    this.tweens.add({
      targets: rocker, a: 0.32, duration: 600, yoyo: true, repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: () => drawGlyph(rocker.a),
    });

    const text = onTouch
      ? 'Tilt your phone\nto steer!'
      : 'Shapes stuck?\nToggle TILT (top right) to\nrotate the container.';
    const label = this.add.text(cardX + 78, cardY + cardH / 2, text, {
      fontFamily: FONT, fontSize: onTouch ? '20px' : '16px', fontStyle: 'bold', color: css(pal.primary),
      resolution: DPR, lineSpacing: 2,
    }).setOrigin(0, 0.5).setDepth(151);

    const all = [card, glyph, label];
    all.forEach((o) => o.setAlpha(0));
    this.tweens.add({ targets: all, alpha: 1, duration: 200 });

    let dismissed = false;
    const dismiss = () => {
      if (dismissed) return;
      dismissed = true;
      GameScene.markTutorialSeen();
      this.tweens.add({
        targets: all, alpha: 0, duration: 200,
        onComplete: () => all.forEach((o) => o.destroy()),
      });
    };

    // Dismiss on tap anywhere, or once the player actually does the thing:
    // tilting the device (touch) / switching the TILT toggle on (desktop).
    this.input.once('pointerdown', dismiss);
    const tiltCheck = this.time.addEvent({
      delay: 100, loop: true, callback: () => {
        const done = onTouch
          ? !!this.deviceTilt?.isEnabled() && Math.abs(this.deviceTilt.getNormalized()) > 0.12
          : isTiltEnabled();
        if (done) {
          tiltCheck.remove();
          dismiss();
        }
      },
    });
    // Safety auto-dismiss so it never blocks play. Desktop gets longer since
    // it asks for a deliberate toggle rather than a reflexive wrist motion.
    this.time.delayedCall(onTouch ? 8000 : 12000, () => { tiltCheck.remove(); dismiss(); });
  }

  private layoutTopRow(_width: number, _y: number): void {
    // Layout is now static — score label/number and next label/preview are
    // positioned at creation time. No dynamic repositioning needed.
  }

  /** Short status string under the tilt icon — keeps the icon clean while
   *  still telling the player whether tilt is live. */
  private tiltStatusGlyph(): string {
    if (!isTiltEnabled()) return 'OFF';
    if (!isTiltDeviceModeEnabled()) return 'ON'; // joystick mode — always active when tilt is on
    const s = this.deviceTilt?.getStatus() ?? 'idle';
    if (s === 'granted') return 'ON';
    if (s === 'denied') return 'NO';
    if (s === 'unsupported') return '—';
    if (s === 'pending') return '...';
    return 'OFF';
  }

  /** Rebuild parts of the HUD that depend on settings the player can toggle
   *  mid-run. Called on scene resume after Settings and after tilt toggled. */
  private applyLiveSettings(): void {
    if (!this.scale) return;
    const pal = activePalette();
    const width = this.scale.width;
    const height = this.scale.height;
    this.buildTopRightButtons(width, this.layoutButtonY);
    // Wheel shows when tilt is on AND mode is joystick (not device orientation).
    const wantWheel = isTiltJoystickActive();
    const hasWheel = !!this.wheel;
    if (wantWheel && !hasWheel) {
      const wheelX = 80;
      const wheelY = height - 70 - readSafeAreaInsetBottom();
      this.wheel = new SteeringWheel(this, wheelX, wheelY, 44);
      this.tiltGaugeBg = this.add
        .rectangle(wheelX, wheelY - 60, 90, 5, pal.surface, 0.75)
        .setOrigin(0.5)
        .setDepth(70);
      this.tiltGaugeFill = this.add
        .rectangle(wheelX - 45, wheelY - 60, 90, 5, pal.primary, 0.9)
        .setOrigin(0, 0.5)
        .setDepth(71);
    } else if (!wantWheel && hasWheel) {
      this.wheel?.destroy();
      this.wheel = undefined;
      this.tiltGaugeBg?.destroy();
      this.tiltGaugeBg = undefined;
      this.tiltGaugeFill?.destroy();
      this.tiltGaugeFill = undefined;
    }
  }

  // Wheel + tilt gauge + container
  private wheel?: SteeringWheel;
  private deviceTilt?: DeviceTilt;

  private tiltButtonLabel(): string {
    const s = this.deviceTilt?.getStatus() ?? 'idle';
    if (s === 'granted') return 'TILT ON';
    if (s === 'denied') return 'TILT DENIED';
    if (s === 'unsupported') return 'NO TILT';
    return 'TAP TO TILT';
  }

  private tiltGauge = 1; // 0..1; drains while rotated away from rest, refills when centered
  private tiltGaugeBg?: Phaser.GameObjects.Rectangle;
  private tiltGaugeFill?: Phaser.GameObjects.Rectangle;
  private walls: {
    body: MatterJS.BodyType;
    visual: Phaser.GameObjects.Rectangle | Phaser.GameObjects.Image;
    restX: number;
    restY: number;
    w: number;
    h: number;
    rim?: Phaser.GameObjects.Rectangle;
    rimOffsetX: number;
    rimOffsetY: number;
  }[] = [];
  private containerPivot: { x: number; y: number } = { x: 0, y: 0 };
  private containerAngle = 0;
  // Inner faces of the U-container in its un-rotated (local) frame. Used by the
  // post-step containment net (containBodies) to detect and undo wall tunneling.
  private innerLeftX = 0;
  private innerRightX = 0;
  private innerFloorY = 0;
  private innerTopY = 0;

  // Drain rate scales with how far the wheel is rotated (full = π → max drain).
  private static readonly TILT_DRAIN = 0.55; // per second at full rotation
  private static readonly TILT_REFILL = 0.20; // per second while at rest

  private earnShakeCharge(): void {
    // Each merge refunds a chunk of tilt gauge as a reward.
    this.tiltGauge = Math.min(1, this.tiltGauge + 0.06);
  }

  private smoothedTiltAngle = 0;

  private updateTilt(delta: number): void {
    const dt = delta / 1000;
    this.wheel?.update(delta);
    const wheelAngle = this.wheel?.getAngle() ?? 0;
    const wheelActive = this.wheel?.isActive() ?? false;
    // Device tilt feeds in if enabled and the player isn't currently dragging
    // the wheel. Low-pass the gamma reading because raw phone sensor data is
    // noisy and produces jerky container motion when fed straight through.
    let deviceAngle = 0;
    let deviceLive = false;
    if (this.deviceTilt?.isEnabled() && !wheelActive) {
      deviceAngle = this.deviceTilt.getNormalized() * (Math.PI / 2);
      deviceLive = true;
    }
    if (deviceLive) {
      const smoothing = 1 - Math.exp(-dt * 8);
      this.smoothedTiltAngle += (deviceAngle - this.smoothedTiltAngle) * smoothing;
    } else {
      this.smoothedTiltAngle = 0;
    }
    const inputAngle = deviceLive ? this.smoothedTiltAngle : wheelAngle;
    // Drain proportional to absolute rotation (0 at rest, max at ±π).
    const intensity = Math.min(1, Math.abs(inputAngle) / Math.PI);
    if (intensity > 0.05 && this.tiltGauge > 0) {
      this.tiltGauge = Math.max(0, this.tiltGauge - GameScene.TILT_DRAIN * intensity * dt);
    } else {
      this.tiltGauge = Math.min(1, this.tiltGauge + GameScene.TILT_REFILL * dt);
    }
    // Target container angle = combined input, scaled to zero when gauge runs out.
    const targetAngle = this.tiltGauge > 0 ? inputAngle : 0;
    const lerp = 1 - Math.exp(-dt * 10);
    let next = this.containerAngle + (targetAngle - this.containerAngle) * lerp;
    // Cap the per-frame angular delta. Matter has no CCD for moving static
    // bodies, so a wall that sweeps further than a body radius in one step
    // can tunnel through dynamic shapes. Limiting the delta keeps the wall's
    // motion small enough between physics steps for the solver to resolve.
    const MAX_ANGLE_DELTA_PER_FRAME = 0.05;
    const delta_ = next - this.containerAngle;
    if (Math.abs(delta_) > MAX_ANGLE_DELTA_PER_FRAME) {
      next = this.containerAngle + Math.sign(delta_) * MAX_ANGLE_DELTA_PER_FRAME;
    }
    this.containerAngle = next;
    this.applyContainerRotation();
    // Update gauge UI + wheel enabled state if either exists.
    if (this.tiltGaugeFill) this.tiltGaugeFill.setScale(this.tiltGauge, 1);
    this.wheel?.setEnabled(this.tiltGauge > 0);
  }

  private afterUpdateHandler?: () => void;

  private applyContainerRotation(): void {
    const cos = Math.cos(this.containerAngle);
    const sin = Math.sin(this.containerAngle);
    const px = this.containerPivot.x;
    const py = this.containerPivot.y;
    for (const w of this.walls) {
      const dx = w.restX - px;
      const dy = w.restY - py;
      const nx = px + dx * cos - dy * sin;
      const ny = py + dx * sin + dy * cos;
      this.matter.body.setPosition(w.body, { x: nx, y: ny });
      this.matter.body.setAngle(w.body, this.containerAngle);
    }
  }

  /**
   * Post-step containment net. Matter has no continuous collision detection for
   * the static container walls — they're teleported via setPosition each frame
   * to rotate the U — so a fast spin can let a shape's center cross a wall plane
   * in a single step and escape out the bottom or sides. The per-frame angle
   * clamp and thick walls reduce this but don't fully prevent it under a heavy,
   * fast whip.
   *
   * After each physics step we transform every dynamic body into the
   * container's local (un-rotated) frame. If a body's center has crossed the
   * inner face of a side wall (only within the wall's vertical span) or the
   * floor (only within the column between the side walls), we push it back to
   * the surface and zero the velocity component pointing further out so it
   * settles instead of bouncing back through. The open top is never clamped, so
   * a shape can still overflow the mouth — preserving the fall-out game-over.
   */
  private containBodies(): void {
    const px = this.containerPivot.x;
    const py = this.containerPivot.y;
    const angle = this.containerAngle;
    const cos = Math.cos(angle);
    const sin = Math.sin(angle);
    const INSET = 2; // seat the center just inside the face; the solver finishes

    for (const b of this.dynamicBodies) {
      if (!b.active || b.merging) continue;
      const body = b.body as MatterJS.BodyType;

      // World -> container-local (inverse rotation about the pivot).
      const dx = body.position.x - px;
      const dy = body.position.y - py;
      let lx = px + dx * cos + dy * sin;
      let ly = py - dx * sin + dy * cos;

      let clampX = 0; // -1 = pushed off the left wall, +1 = off the right wall
      let clampFloor = false;

      // Side walls only exist between the mouth and the floor; above the mouth
      // the top is open, so don't fence shapes in there (let them overflow).
      if (ly >= this.innerTopY && ly <= this.innerFloorY) {
        if (lx < this.innerLeftX) {
          lx = this.innerLeftX + INSET;
          clampX = -1;
        } else if (lx > this.innerRightX) {
          lx = this.innerRightX - INSET;
          clampX = 1;
        }
      }
      // Floor only catches shapes inside the column between the side walls, so a
      // shape that legitimately spilled out the top isn't yanked back from below.
      if (lx > this.innerLeftX && lx < this.innerRightX && ly > this.innerFloorY) {
        ly = this.innerFloorY - INSET;
        clampFloor = true;
      }

      if (!clampX && !clampFloor) continue;

      // Local -> world for the corrected position.
      const blx = lx - px;
      const bly = ly - py;
      this.matter.body.setPosition(body, {
        x: px + blx * cos - bly * sin,
        y: py + blx * sin + bly * cos,
      });

      // Kill the outward velocity component (in local axes) so the shape doesn't
      // immediately re-penetrate on the next step — that would read as jitter.
      const v = body.velocity;
      let vlx = v.x * cos + v.y * sin;
      let vly = -v.x * sin + v.y * cos;
      if (clampX < 0 && vlx < 0) vlx = 0;
      if (clampX > 0 && vlx > 0) vlx = 0;
      if (clampFloor && vly > 0) vly = 0;
      this.matter.body.setVelocity(body, {
        x: vlx * cos - vly * sin,
        y: vlx * sin + vly * cos,
      });
    }
  }

  private debugSpawnBurst(leftX: number, rightX: number): void {
    const count = 30;
    for (let i = 0; i < count; i++) {
      this.time.delayedCall(i * 60, () => {
        if (this.gameOver) return;
        const x = Phaser.Math.Between(leftX + 40, rightX - 40);
        const tierIndex = Phaser.Math.Between(0, 3);
        const body = createShape(this, tierIndex, x, this.layoutSpawnY + 10);
        body.setDepth(15);
        this.dynamicBodies.push(body);
      });
    }
  }

  private spawnScoreParticles(): void {
    const x = this.scoreText.x;
    const y = this.scoreText.y;
    for (let i = 0; i < 6; i++) {
      const g = this.add.graphics().setDepth(70);
      g.fillStyle(0xffffff, 1);
      g.fillCircle(0, 0, 2 + Math.random() * 2);
      g.setPosition(x + (Math.random() - 0.5) * 20, y);
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.9;
      const dist = 24 + Math.random() * 28;
      this.tweens.add({
        targets: g,
        x: g.x + Math.cos(angle) * dist,
        y: g.y + Math.sin(angle) * dist,
        alpha: 0,
        duration: 380 + Math.random() * 180,
        ease: 'Quad.easeOut',
        onComplete: () => g.destroy(),
      });
    }
  }

  private spawnShapeReactions(x: number, y: number, tierIndex: number): void {
    const TIER_EMOJIS: string[][] = [
      ['🔺', '🔺', '🔺'],                   // 0: triangle
      ['🟦', '🔲', '🟦'],                   // 1: square
      ['❤️', '🩷', '💖', '💗', '💕', '💓'], // 2: heart
      ['🔶', '🔸', '⭐'],                   // 3: pentagon
      ['🌙', '🌙', '✨'],                   // 4: crescent
      ['🔷', '💠', '🔷'],                   // 5: hexagon
      ['➕', '➕', '✚'],                   // 6: plus
      ['⭐', '💫', '✨', '🌟'],            // 7: star
      ['🛑', '⛔', '🔴'],                   // 8: octagon
      ['👑', '💎', '🏆', '✨'],            // 9: circle/KING
    ];
    const emojis = TIER_EMOJIS[Math.min(tierIndex, TIER_EMOJIS.length - 1)];
    const count = 4 + Math.floor(Math.random() * 3);
    for (let i = 0; i < count; i++) {
      this.time.delayedCall(i * 70 + Math.random() * 40, () => {
        if (this.gameOver) return;
        const emoji = emojis[Math.floor(Math.random() * emojis.length)];
        const size = 20 + Math.random() * 14;
        const startX = x + (Math.random() - 0.5) * 50;
        const startY = y + (Math.random() - 0.5) * 20;
        const t = this.add
          .text(startX, startY, emoji, { fontSize: `${size}px` })
          .setOrigin(0.5)
          .setDepth(35)
          .setAlpha(0)
          .setScale(0.4);
        const driftX = (Math.random() - 0.5) * 70;
        const riseY = 130 + Math.random() * 90;
        this.tweens.add({
          targets: t,
          alpha: 1,
          scale: 1.1,
          duration: 120,
          ease: 'Back.easeOut',
          onComplete: () => {
            this.tweens.add({
              targets: t,
              y: startY - riseY,
              x: startX + driftX,
              alpha: 0,
              scale: 1.35,
              duration: 900 + Math.random() * 400,
              ease: 'Cubic.easeOut',
              onComplete: () => t.destroy(),
            });
          },
        });
      });
    }
  }

  /** A safe scale "pop": always resets to the absolute base scale first and
   *  kills any in-flight tween, so rapid re-triggers (e.g. a fast coin streak)
   *  can never compound a relative multiplier into a runaway scale. */
  private popObject(
    obj: Phaser.GameObjects.Components.Transform & Phaser.GameObjects.GameObject,
    base: number,
    factor: number,
  ): void {
    if (!obj?.active) return;
    this.tweens.killTweensOf(obj);
    obj.setScale(base);
    this.tweens.add({
      targets: obj,
      scaleX: base * factor,
      scaleY: base * factor,
      duration: 90,
      yoyo: true,
      ease: 'Back.easeOut',
      onComplete: () => {
        if (obj.active) obj.setScale(base);
      },
    });
  }

  private comboGlow?: Phaser.GameObjects.Image;

  /** A brief warm glow bloom for combo escalation. Reuses a single radial-glow
   *  image (transparent edges, so it can never wash the playfield to a flat
   *  color the way a full-screen fill could), re-triggered rather than stacked. */
  private flashComboGlow(combo: number): void {
    const { width, height } = this.scale;
    if (!this.comboGlow || !this.comboGlow.active) {
      this.comboGlow = addRadialGlow(this, width / 2, height * 0.5, Math.max(width, height) * 0.6, activePalette().coin, 0, 49);
    }
    const g = this.comboGlow;
    const a = Math.min(0.1 + combo * 0.03, 0.34);
    this.tweens.killTweensOf(g);
    g.setAlpha(a);
    this.tweens.add({ targets: g, alpha: 0, duration: 340, ease: 'Cubic.easeOut' });
  }

  /** Draw a glossy candy "chip": soft drop shadow, dark glassy body, accent
   *  outline, and a top sheen. Used for the score and coin HUD lozenges. */
  private drawCandyChip(
    g: Phaser.GameObjects.Graphics,
    cx: number,
    cy: number,
    w: number,
    h: number,
    accent: number,
  ): void {
    const x = cx - w / 2;
    const y = cy - h / 2;
    const r = h / 2;
    const pal = activePalette();
    g.clear();
    g.fillStyle(pal.chipShadow, 0.35);
    g.fillRoundedRect(x, y + 3, w, h, r);
    g.fillStyle(pal.chipFill, 0.92);
    g.fillRoundedRect(x, y, w, h, r);
    g.lineStyle(1.5, accent, 0.5);
    g.strokeRoundedRect(x, y, w, h, r);
    g.fillStyle(0xffffff, 0.1);
    g.fillRoundedRect(x + 3, y + 2, w - 6, h * 0.42, r * 0.8);
  }

  private renderNextPreview(tierIndex: number): void {
    if (this.nextPreviewSprite) this.nextPreviewSprite.destroy();
    if (this.nextPreviewGlow) {
      this.tweens.killTweensOf(this.nextPreviewGlow);
      this.nextPreviewGlow.destroy();
    }
    const tier = TIERS[tierIndex];
    const x = this.layoutNextX;
    const y = this.layoutNextShapeY;
    // Soft candy glow halo behind the preview, tinted to the upcoming tier.
    this.nextPreviewGlow = addRadialGlow(this, x, y, 24, tier.color, 0.55, 59);
    this.tweens.add({
      targets: this.nextPreviewGlow,
      alpha: 0.8,
      scale: 1.18,
      duration: 950,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
    const previewScale = Math.min(1, 18 / tier.radius);
    this.nextPreviewSprite = this.add
      .image(x, y, textureKey(tierIndex))
      .setOrigin(0.5)
      .setScale(previewScale)
      .setDepth(60);
    // Y will sync to bobDriver on next frame — no separate tween needed.
  }

  update(time: number, delta: number): void {
    if (this.gameOver) return;
    this.updateTilt(delta);
    this.dynamicBodies = this.dynamicBodies.filter((b) => b.active);
    // First-run tilt tutorial. Fires once when the pile reaches 12 shapes —
    // late enough that the player has actually played a while and likely has
    // a few stuck shapes that tilting would help with.
    if (!this.tutorialFired && this.dynamicBodies.length >= 12 && !GameScene.tutorialSeen()) {
      this.tutorialFired = true;
      this.runTiltTutorial();
    }
    // Fall-out detection: any body that escapes the container ends the game.
    // Margin keeps a small grace zone for shapes briefly outside before physics
    // pushes them back in.
    const { width, height } = this.scale;
    for (const b of this.dynamicBodies) {
      if (!b.active || b.merging) continue;
      if (b.y > height + 80 || b.x < -60 || b.x > width + 60) {
        if (this.escapeImmunity) {
          b.destroy();
          continue;
        }
        this.endGame('fall-out', b.shapeTier);
        return;
      }
    }
    this.deadLine.update(time, this.dynamicBodies);
  }

  private showKingPopup(px: number, py: number): void {
    const { width, height } = this.scale;
    const cx = width / 2, cy = height / 2;
    void px; void py;
    const pal = activePalette();

    const dim = this.add.rectangle(0, 0, width, height, 0x000000, 0.6)
      .setOrigin(0).setDepth(200).setAlpha(0);

    const kingTier = TIERS[TIERS.length - 1];
    const kingDisplayR = Math.min(width * 0.18, 80);
    const kingScale = (kingDisplayR * 2) / (kingTier.radius * 2 + 6);
    const kingSprite = this.add.image(cx, cy - 44, textureKey(TIERS.length - 1))
      .setScale(0).setDepth(201).setAlpha(0);

    const crown = this.add.text(cx, cy - 110, '👑', { fontSize: '48px' })
      .setOrigin(0.5).setDepth(201).setAlpha(0).setScale(0.3);

    const titleText = this.add.text(cx, cy + 30, 'K I N G !', {
      fontFamily: FONT, fontSize: '44px', fontStyle: 'bold',
      color: css(pal.coin), resolution: DPR,
    }).setOrigin(0.5).setDepth(201).setAlpha(0).setScale(0.5);

    const subText = this.add.text(cx, cy + 82, 'Maximum evolution', {
      fontFamily: FONT, fontSize: '17px', color: css(pal.text), resolution: DPR,
    }).setOrigin(0.5).setDepth(201).setAlpha(0);

    this.tweens.add({ targets: dim, alpha: 1, duration: 180 });
    this.tweens.add({ targets: crown, alpha: 1, scale: 1, duration: 350, delay: 60, ease: 'Back.easeOut' });
    this.tweens.add({ targets: kingSprite, alpha: 1, scale: kingScale, duration: 420, delay: 160, ease: 'Back.easeOut' });
    this.tweens.add({ targets: titleText, alpha: 1, scale: 1, duration: 300, delay: 300, ease: 'Back.easeOut' });
    this.tweens.add({ targets: subText, alpha: 1, duration: 220, delay: 420 });

    const royalEmojis = ['👑', '💎', '🏆', '✨', '🌟', '💫', '⚜️'];
    for (let i = 0; i < 22; i++) {
      this.time.delayedCall(80 + i * 55, () => {
        if (this.gameOver && i > 3) return;
        const emoji = royalEmojis[Math.floor(Math.random() * royalEmojis.length)];
        const ex = cx + (Math.random() - 0.5) * width * 0.85;
        const ey = cy + (Math.random() - 0.5) * height * 0.55;
        const e = this.add.text(ex, ey, emoji, { fontSize: `${18 + Math.random() * 26}px` })
          .setOrigin(0.5).setDepth(202).setAlpha(0).setScale(0.3);
        this.tweens.add({
          targets: e, alpha: 0.95, scale: 1.2, duration: 180, ease: 'Back.easeOut',
          onComplete: () => this.tweens.add({
            targets: e, y: ey - 80, alpha: 0, duration: 1100, ease: 'Quad.easeOut',
            onComplete: () => e.destroy(),
          }),
        });
      });
    }

    const all = [dim, crown, kingSprite, titleText, subText];
    this.time.delayedCall(2600, () => {
      this.tweens.add({
        targets: all, alpha: 0, duration: 280,
        onComplete: () => all.forEach(o => { if (o.active) o.destroy(); }),
      });
    });
  }

  private endGame(reason: 'dead-line' | 'fall-out' = 'dead-line', escapedTier?: number): void {
    if (this.gameOver) return;
    this.gameOver = true;
    this.spawner.enabled = false;
    sfx('gameOver');
    track('game_over', { score: this.score.current, reason });
    addToAllTimeMergeCounts(this.score.mergeCounts);
    this.scene.launch('GameOverScene', {
      score: this.score.current,
      highScore: this.score.getHighScore(),
      reason,
      escapedTier,
      mergeCounts: { ...this.score.mergeCounts },
    });
    this.scene.pause();
  }
}
